import { useCallback, useEffect, useRef } from 'react';
import { ApiRequestError } from './api';

/**
 * A publish whose answer never came.
 *
 * Publishing can outlast the connection: a large PDF or a video takes minutes, the platform in front
 * of the API cuts the request, and the API carries on. The page then holds a failure for a post that
 * may well go out. So a request that failed WITHOUT an answer from our API is not reported as
 * "Publish failed": the page says contact was lost, then reads the post again every ten seconds, for
 * up to six minutes, and shows what really happened. It only ever READS the post - it never sends
 * the publish again.
 */

/** Said in place of the failure while the post's real outcome is not known yet. */
export const LOST_REQUEST_MESSAGE =
  'We lost contact while this post was publishing. It may still be going out. This page will show the result.';

/** How often the post is read again, and for how long. */
export const LOST_POLL_EVERY_MS = 10_000;
export const LOST_POLL_FOR_MS = 6 * 60_000;

/**
 * Whether a request failed without an answer from our API - as opposed to being refused by it.
 *
 * Our API answers every refusal with its error envelope, which `request` (api.ts) throws as an
 * `ApiRequestError` carrying that `body`. Lost is everything short of that: nothing came back at all
 * (the `ApiRequestError` with status 0 that `request` throws when `fetch` itself fails), something
 * other than our API answered with no envelope (a gateway's bare 502 / 504), or with a page that is
 * not JSON (which `request` cannot parse: a SyntaxError). Any other error was thrown by the page's
 * own code before a request was made, and is not a lost request.
 */
export function isLostRequest(err: unknown): boolean {
  if (err instanceof ApiRequestError) return err.status === 0 || err.body === undefined;
  return err instanceof SyntaxError;
}

/** A publish request that was lost, and the post it was for - so the page knows which to watch. */
export class LostPublishError extends Error {
  readonly postId: string;

  constructor(postId: string) {
    super(LOST_REQUEST_MESSAGE);
    this.name = 'LostPublishError';
    this.postId = postId;
  }
}

/**
 * Send a publish for `postId`. A lost one is thrown as a {@link LostPublishError}; a refusal - and
 * anything else - as it came. For a caller whose error handler is not handed the post's id.
 */
export async function sendPublish<T>(postId: string, send: () => Promise<T>): Promise<T> {
  try {
    return await send();
  } catch (err) {
    if (isLostRequest(err)) throw new LostPublishError(postId);
    throw err;
  }
}

/** What of a post says how its publish ended. */
export interface PublishOutcome {
  publishedAt: string | null;
  publishError: string | null;
}

/**
 * Whether `post` shows how the lost publish ended: it is out, or it carries a failure that was not
 * there when the publish was sent (`errorBefore`).
 *
 * A failure that is merely GONE is not an end. No publish ends by clearing the old failure and
 * leaving the post unpublished: a success clears it together with setting `publishedAt`, a failure
 * writes its own reason. What does clear it alone is the save in front of the publish (a change of
 * platform drops the schedule, and the failure with it) - and that publish is still on its way.
 */
export function publishEnded(post: PublishOutcome, errorBefore: string | null): boolean {
  return post.publishedAt != null || (post.publishError != null && post.publishError !== errorBefore);
}

export interface LostPublishWatch<P extends PublishOutcome> {
  /** Read the post again. A read that fails is one more lost request: the watch goes on. */
  read: () => Promise<P>;
  /** The post's `publishError` when the publish was sent. */
  errorBefore: string | null;
  /** The post shows how the publish ended. Called once; the watch is over. */
  onResult: (post: P) => void;
  /** Six minutes went by and the post still shows neither. Called once; the watch is over. */
  onGiveUp?: () => void;
}

/**
 * Read the post every {@link LOST_POLL_EVERY_MS} until it shows how the publish ended, or until
 * {@link LOST_POLL_FOR_MS} has gone by. Returns what stops the watch early (the page went away).
 */
export function watchLostPublish<P extends PublishOutcome>(watch: LostPublishWatch<P>): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let waited = 0;

  const look = async () => {
    timer = null;
    waited += LOST_POLL_EVERY_MS;
    let post: P | null = null;
    try {
      post = await watch.read();
    } catch {
      post = null;
    }
    if (stopped) return;
    if (post && publishEnded(post, watch.errorBefore)) {
      stopped = true;
      watch.onResult(post);
      return;
    }
    if (waited >= LOST_POLL_FOR_MS) {
      stopped = true;
      watch.onGiveUp?.();
      return;
    }
    timer = setTimeout(() => void look(), LOST_POLL_EVERY_MS);
  };
  timer = setTimeout(() => void look(), LOST_POLL_EVERY_MS);

  return () => {
    stopped = true;
    if (timer != null) clearTimeout(timer);
  };
}

/**
 * {@link watchLostPublish} for a component: every watch it starts stops when the component goes away.
 * The function returned is the same on every render.
 */
export function useLostPublishWatch(): <P extends PublishOutcome>(watch: LostPublishWatch<P>) => void {
  const stops = useRef(new Set<() => void>());
  useEffect(() => {
    const running = stops.current;
    return () => {
      for (const stop of running) stop();
      running.clear();
    };
  }, []);
  return useCallback(<P extends PublishOutcome>(watch: LostPublishWatch<P>) => {
    const stop = watchLostPublish<P>({
      ...watch,
      onResult: (post) => {
        stops.current.delete(stop);
        watch.onResult(post);
      },
      onGiveUp: () => {
        stops.current.delete(stop);
        watch.onGiveUp?.();
      },
    });
    stops.current.add(stop);
  }, []);
}
